/**
 * Calculate deliverability score based on configuration and performance
 */
export const calculateDeliverabilityScore = (settings, campaigns) => {
  const hasAuthConfig = settings?.spf_configured || settings?.dkim_configured || settings?.dmarc_configured;
  const hasCampaignData = campaigns.length > 0;

  // If no infrastructure configured and no campaign data yet, we can't produce a meaningful score
  if (!hasAuthConfig && !hasCampaignData) {
    return null;
  }

  let score = 60; // Base score once we have some signal to work with
  
  // Authentication checks
  if (settings.spf_configured) score += 10;
  if (settings.dkim_configured) score += 15;
  if (settings.dmarc_configured) score += 10;
  
  // Sender email check
  if (!settings.from_email.includes('noreply')) score += 5;
  
  // Recent campaign performance
  if (campaigns.length > 0) {
    const recentCampaigns = campaigns.slice(0, 5);
    
    // Average delivery rate
    const avgDeliveryRate = recentCampaigns.reduce(
      (acc, c) => acc + (c.delivered / c.sent), 0
    ) / recentCampaigns.length;
    
    // Average complaint rate
    const avgComplaintRate = recentCampaigns.reduce(
      (acc, c) => acc + (c.complained / c.sent), 0
    ) / recentCampaigns.length;
    
    // Bonus for good delivery rate
    if (avgDeliveryRate > 0.95) score += 5;
    
    // Bonus for low complaint rate
    if (avgComplaintRate < 0.001) score += 5;
    
    // Penalty for high complaint rate
    if (avgComplaintRate > 0.005) score -= 10;
  }
  
  return Math.min(100, Math.max(0, score));
};

/**
 * Create campaign with realistic metrics
 */
export const createCampaignMetrics = (recipientCount, settings) => {
  // Base delivery rate with authentication bonus
  const baseDeliveryRate = 0.97;
  const authBonus = ((settings?.spf_configured ? 0.01 : 0) + 
                    (settings?.dkim_configured ? 0.01 : 0) + 
                    (settings?.dmarc_configured ? 0.005 : 0));
  const deliveryRate = Math.min(0.99, baseDeliveryRate + authBonus);
  
  const sent = recipientCount;
  const delivered = Math.floor(sent * deliveryRate);
  const bounced = sent - delivered;
  
  // Email-specific metrics
  const openRate = 0.25 + Math.random() * 0.25; // 25-50%
  const clickRate = 0.05 + Math.random() * 0.10; // 5-15% of delivered
  const opened = Math.floor(delivered * openRate);
  const clicked = Math.floor(opened * (clickRate / openRate));
  
  // SMS-specific metrics
  const replyRate = 0.10 + Math.random() * 0.15; // 10-25%
  const replied = Math.floor(delivered * replyRate);
  
  // Other metrics
  const unsubscribed = Math.floor(sent * 0.002); // 0.2%
  const complained = Math.floor(sent * 0.001); // 0.1%
  
  return {
    sent,
    delivered,
    bounced,
    opened,
    clicked,
    replied,
    unsubscribed,
    complained
  };
};

/**
 * Format campaign data for export
 */
export const formatCampaignForExport = (campaign) => {
  return {
    'Campaign Name': campaign.name,
    'Channel': campaign.channel,
    'Template': campaign.template,
    'Sent': campaign.sent,
    'Delivered': campaign.delivered,
    'Delivery Rate': ((campaign.delivered / campaign.sent) * 100).toFixed(2) + '%',
    'Opened': campaign.opened,
    'Open Rate': campaign.channel === 'email' 
      ? ((campaign.opened / campaign.delivered) * 100).toFixed(2) + '%' 
      : 'N/A',
    'Clicked': campaign.clicked,
    'Click Rate': campaign.channel === 'email' && campaign.opened > 0
      ? ((campaign.clicked / campaign.opened) * 100).toFixed(2) + '%' 
      : 'N/A',
    'Replied': campaign.replied,
    'Reply Rate': campaign.channel === 'sms' 
      ? ((campaign.replied / campaign.delivered) * 100).toFixed(2) + '%' 
      : 'N/A',
    'Bounced': campaign.bounced,
    'Unsubscribed': campaign.unsubscribed,
    'Complained': campaign.complained,
    'Status': campaign.status,
    'Created': new Date(campaign.createdAt).toLocaleString(),
    'Sent Date': campaign.sentDate ? new Date(campaign.sentDate).toLocaleString() : 'Scheduled'
  };
};
